import React, { useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Layout from '@components/Layout';
import Colors from '@components/shared/Colors';
import ArticlePageContent from '@components/vitals/ArticlePageContent';
import { DEFAULT_VITALS_LOCALE, NAVBAR_HEIGHT } from '@constants';
import { Article, Category } from '@contracts';
import { getArticleInfo, getAudio } from '@prismicVitals';

export const pageQuery = graphql`
  query Articles($uid: String!, $language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    allPrismicVitalsCategories {
      edges {
        node {
          uid
          id
          data {
            category_image {
              url
            }
            meta_title
            meta_description
            category_color
            tag_name_color
            image_background_color
            font_color
            title
            category_description
          }
        }
      }
    }
    allPrismicVitalsArticles(filter: { uid: { eq: $uid } }) {
      nodes {
        id
        uid
        url
        lang
        first_publication_date
        tags
        data {
          article_subheadline
          featured_image {
            url
            gatsbyImageData
            alt
          }
          author {
            slug
          }
          meta_description
          meta_title
          meta_image {
            url
          }
          title
          article_read_time
          category {
            slug
            id
            uid
          }
          audio {
            id
          }
          hero_credits {
            richText
          }
          authors {
            article_author {
              uid
            }
          }
          medical_reviewers {
            medical_reviewer {
              uid
            }
          }
          illustrators {
            illustrator {
              uid
            }
          }
          body {
            ... on PrismicVitalsArticlesDataBodyCta {
              slice_type
              id
            }
            ... on PrismicVitalsArticlesDataBodyFullscreenImage {
              primary {
                fw_image {
                  gatsbyImageData
                  alt
                  url
                }
              }
              id
            }
            ... on PrismicVitalsArticlesDataBodyHorizontalRule {
              id
              slice_type
            }
            ... on PrismicVitalsArticlesDataBodyQuestionAndAnswer {
              id
              slice_type
              primary {
                subject_initial
                question
                qa_answer {
                  richText
                  text
                }
                interviewer_initial
              }
            }
            ... on PrismicVitalsArticlesDataBodyQuote {
              id
              slice_type
              primary {
                quote_author
                quote {
                  richText
                  text
                }
              }
            }
            ... on PrismicVitalsArticlesDataBodyQuoteWithImage {
              id
              primary {
                qi_quote_image {
                  gatsbyImageData
                  alt
                  url
                }
                qi_quote_author
                qi_quote {
                  text
                  richText
                }
                qi_layout
              }
              slice_type
            }
            ... on PrismicVitalsArticlesDataBodyRichText {
              id
              slice_type
              primary {
                rich_text {
                  text
                  richText
                }
              }
            }
            ... on PrismicVitalsArticlesDataBodyVideoEmbed {
              id
              slice_type
              primary {
                video_url
                video_caption
              }
            }
            ... on PrismicVitalsArticlesDataBodyTwoImages {
              id
              slice_type
              primary {
                ti_right_image {
                  alt
                  url
                  gatsbyImageData
                }
                ti_left_image {
                  url
                  gatsbyImageData
                  alt
                }
              }
            }
            ... on PrismicVitalsArticlesDataBodySingleImage {
              id
              slice_type
              primary {
                si_image {
                  gatsbyImageData
                  url
                  alt
                }
              }
            }
            ... on PrismicVitalsArticlesDataBodyShoppingModule {
              id
              slice_type
              primary {
                banner_type
              }
            }
            ... on PrismicVitalsArticlesDataBodyShoppingBanner {
              id
              slice_type
              primary {
                banner {
                  uid
                }
                sb_mobile_view
              }
            }
            ... on PrismicVitalsArticlesDataBodyProductCards {
              id
              slice_type
              primary {
                related_products_title
              }
              items {
                related_product_code
              }
            }
            ... on PrismicVitalsArticlesDataBodyRichTextWithStatistic {
              id
              slice_type
              primary {
                statistic_layout
                stat_small_text
                stat_large_text
                stat_description
                rts_rich_text {
                  text
                  richText
                }
              }
            }
            ... on PrismicVitalsArticlesDataBodyRichTextWithQuote {
              id
              slice_type
              primary {
                rtq_wysiwyg {
                  text
                  richText
                }
                rtq_quote_author
                rtq_quote {
                  text
                  richText
                }
                rtq_layout
              }
            }
            ... on PrismicVitalsArticlesDataBodyRichTextWithImage {
              id
              slice_type
              primary {
                rti_rich_text {
                  text
                  richText
                }
                rti_image {
                  url
                  gatsbyImageData
                  alt
                }
                rit_layout
              }
            }
            ... on PrismicVitalsArticlesDataBodyNewsletter {
              id
              slice_type
            }
            ... on PrismicVitalsArticlesDataBodyCaseStudySection {
              slice_type
              items {
                research_description {
                  richText
                }
                link {
                  richText
                }
                research_title {
                  text
                }
                researcher {
                  text
                }
              }
              primary {
                color
                background_color
                padding {
                  text
                }
                section_title {
                  text
                }
              }
            }
            ... on PrismicVitalsArticlesDataBodyVmsFoundersResults {
              id
              slice_type
            }
          }
        }
      }
    }
  }
`;

interface ArticlePageObject {
  allPrismicVitalsArticles: {
    nodes: Article[];
  };
  allPrismicVitalsCategories: {
    edges: Category[];
  };
}

interface Props {
  data: ArticlePageObject;
  pageContext: {
    language: string;
  };
}

const ArticlePage: React.FC<Props> = ({ data, pageContext }) => {
  const { language } = pageContext;

  const getPageData = () => {
    const articles = data.allPrismicVitalsArticles.nodes;
    const currentLocale = articles.find((article) => article.lang === language);
    const fallbackLocale = articles.find((article) => article.lang === DEFAULT_VITALS_LOCALE);
    return currentLocale || fallbackLocale;
  };

  const [articlePageObject, setArticlePageObject] = useState(getPageData());

  // Update articlePageObject when data changes (for previews)
  useEffect(() => {
    setArticlePageObject(getPageData());
  }, [data.allPrismicVitalsArticles]);

  const { allPrismicVitalsCategories } = data;

  if (!articlePageObject) return null;

  const { uid, url, data: articleData } = articlePageObject;
  const { meta_description, meta_title, meta_image, title, category, featured_image } = articleData;

  const categories = allPrismicVitalsCategories.edges;
  const currentCategory = categories.find((item) => {
    if (item.node.uid === 'womens-health' && category.slug === 'health') {
      return true;
    }
    return item.node.uid === category.slug;
  });

  const SEO = {
    uid,
    href: url,
    title: meta_title || title,
    image: meta_image.url,
    description: meta_description || '',
    articleData,
    preloadImage: featured_image.url
  };

  const [activeAudio, setActiveAudio] = useState<any>('');
  const [allArticleInfo, setArticleInfo] = useState<any[]>();
  const [currentArticleIndex, setCurrentArticleIndex] = useState<number>(0);
  const [totalNumberOfArticles, setTotalNumberOfArticles] = useState<number>(0);
  const articleContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    getArticleInfo(setArticleInfo);
    if (allArticleInfo) {
      setCurrentArticleIndex(findCurrentArticleIndex());
    }
  }, []);

  useEffect(() => {
    if (articlePageObject && articleData.audio?.id) {
      getAudio(articleData.audio.id, setActiveAudio);
    }
  }, [articlePageObject]);

  useEffect(() => {
    if (allArticleInfo) {
      setCurrentArticleIndex(findCurrentArticleIndex());
      setTotalNumberOfArticles(allArticleInfo.length - 1);
    }
  }, [uid, allArticleInfo]);

  const findCurrentArticleIndex = () => {
    return allArticleInfo ? allArticleInfo.findIndex((x) => x.uid === uid) : 0;
  };

  if (!currentCategory) {
    return null;
  }
  const { category_color, font_color } = currentCategory?.node?.data ?? {};

  return (
    <Layout SEO={SEO}>
      <ArticlePageContainer $bgColor={category_color} $fontColor={font_color}>
        <ArticlePageContent
          url={url}
          article={articlePageObject}
          currentArticleIndex={currentArticleIndex}
          totalNumberOfArticles={totalNumberOfArticles}
          activeAudio={activeAudio}
          category={currentCategory.node}
          articleContentRef={articleContentRef}
        />
      </ArticlePageContainer>
    </Layout>
  );
};

ArticlePage.propTypes = {
  data: PropTypes.shape({
    allPrismicVitalsArticles: PropTypes.object.isRequired,
    allPrismicVitalsCategories: PropTypes.object.isRequired
  }).isRequired,
  pageContext: PropTypes.shape({
    language: PropTypes.string.isRequired
  }).isRequired
};

export default ArticlePage;

interface ArticlePageContainerProps {
  $bgColor?: string;
  $fontColor?: string;
}

const ArticlePageContainer = styled.div<ArticlePageContainerProps>`
  margin: 0;
  margin-top: ${NAVBAR_HEIGHT}px;
  min-height: 100vh;
  height: auto;
  width: 100%;
  text-align: center;
  transition: opacity 0.5s ease-in, visibility 0.1s ease-in;
  background-color: ${({ $bgColor }) => $bgColor ?? Colors.ecru};
  color: ${({ $fontColor }) => $fontColor ?? Colors.navyBlue};
`;
